import { StatusText } from './api';

/**
 * Der Zustand eines Exportauftrags - EINE Stelle für „läuft er noch?“ und
 * „was darf man mit ihm tun?“ (AUF1, 20.10.2026).
 *
 * ════════════════════════════════════════════════════════════════════════
 * WARUM ES DIESE DATEI GIBT
 * ════════════════════════════════════════════════════════════════════════
 *
 * Am 20.10.2026 wollte der Eigentümer wartende Aufträge abbrechen: „abbrechen
 * button gibt es nicht“. Die Seite Auftragsdetail entschied über die Knöpfe mit
 * `Number(kopf.status) < 50`. Die Detailantwort liefert den Status aber als
 * NAMEN (`JobEndpoints.cs`, `job.Status.ToString()` → „Queued“, „Running“).
 * `Number("Queued")` ist `NaN`, `NaN < 50` ist falsch - die Steuerung war damit
 * bei KEINEM Auftrag sichtbar, seit es sie gibt.
 *
 * Andere Antworten liefern den Status als Zahl (`/models/{id}/exports`:
 * `(int)Status`). Deshalb nimmt jede Funktion hier beides an: den Namen, die
 * Zahl und die Zahl als Text.
 *
 * ════════════════════════════════════════════════════════════════════════
 * DIE REGELN SIND DIE DES SERVERS
 * ════════════════════════════════════════════════════════════════════════
 *
 * `JobEndpoints.cs` (cancel, pausieren, fortsetzen, DELETE), Stand v0.52.15:
 *
 *   Abbrechen   jeder nicht beendete Auftrag
 *   Pausieren   nicht beendet, nicht pausiert, kein Teilauftrag in Verarbeitung
 *   Fortsetzen  pausiert und nicht beendet
 *   Löschen     nicht beendet, kein Teilauftrag in Verarbeitung
 *
 * „In Verarbeitung“ heißt dort: weder `Queued` noch beendet. Ein schon fertiger
 * oder abgebrochener Teilauftrag hält das Pausieren also nicht auf. Die Seite
 * bietet genau das an, was der Server annimmt - nicht mehr und nicht weniger.
 */

/** Die Statuswerte der Plattform als Zahl und Name (`JobStatus.cs`). Queued ist 0. */
export const AUFTRAG_STATUS_NAMEN: Readonly<Record<number, string>> = {
  0: 'Queued',
  10: 'Preparing',
  20: 'Running',
  30: 'Validating',
  40: 'Packaging',
  50: 'Succeeded',
  60: 'Failed',
  70: 'Cancelled',
  80: 'TimedOut',
};

/** Beendet - `JobStatusExtensions.IsTerminal` auf dem Server. */
export const AUFTRAG_BEENDET: ReadonlySet<string> = new Set([
  'Succeeded', 'Failed', 'Cancelled', 'TimedOut',
]);

/**
 * Der Statusname zu einem Wert aus einer Antwort: Name bleibt Name, Zahl (auch
 * als Text) wird zum Namen. Leer oder fehlend ergibt `null` - dann gibt es
 * nichts zu beurteilen und keine Steuerung.
 */
export function auftragStatusName(status: unknown): string | null {
  if (status === null || status === undefined) return null;
  const text = String(status).trim();
  if (text === '') return null;
  const zahl = Number(text);
  if (Number.isInteger(zahl) && AUFTRAG_STATUS_NAMEN[zahl] !== undefined) {
    return AUFTRAG_STATUS_NAMEN[zahl];
  }
  return text;
}

/** Beendet: Succeeded, Failed, Cancelled, TimedOut (Name oder Zahl). */
export function auftragBeendet(status: unknown): boolean {
  const name = auftragStatusName(status);
  return name !== null && AUFTRAG_BEENDET.has(name);
}

/**
 * Nicht beendet: jeder bekannte oder unbekannte Status außer den beendeten.
 *
 * Bewusst NICHT „Status steht in einer Liste laufender Zustände“: Ein Zustand,
 * den der Server später dazunimmt, ließe die Steuerung sonst wieder still
 * verschwinden - genau der Fehler, den diese Datei behebt. Der Server weist eine
 * Aktion an einem beendeten Auftrag ohnehin mit 409 ab.
 */
export function auftragOffen(status: unknown): boolean {
  const name = auftragStatusName(status);
  return name !== null && !AUFTRAG_BEENDET.has(name);
}

/** Der Anzeigetext (`StatusText`), für Name und Zahl gleich. */
export function auftragStatusText(status: unknown): string {
  const name = auftragStatusName(status);
  if (name === null) return '—';
  return StatusText[name] ?? StatusText[String(status)] ?? name;
}

/** Ein Teilauftrag in Verarbeitung: weder `Queued` noch beendet (Serverregel). */
export function teilauftragInVerarbeitung(status: unknown): boolean {
  return auftragOffen(status) && auftragStatusName(status) !== 'Queued';
}

/** Was die Seite anbietet - und ob es gerade wirkt. */
export interface Auftragssteuerung {
  /** Der Auftrag ist nicht beendet: Steuerung zeigen. */
  zeigen: boolean;
  /** Mindestens ein Teilauftrag wird verarbeitet. */
  wirdVerarbeitet: boolean;
  pausieren: boolean;
  fortsetzen: boolean;
  abbrechen: boolean;
  loeschen: boolean;
}

/**
 * Die Steuerung eines Auftrags nach den Regeln des Servers (siehe oben).
 * Ein beendeter Auftrag bekommt keine Knöpfe; die übrigen bekommen genau die,
 * die der Server annimmt.
 */
export function auftragssteuerung(
  kopf: { status: unknown; istPausiert?: boolean | null },
  teile?: ReadonlyArray<{ status: unknown }> | null,
): Auftragssteuerung {
  const zeigen = auftragOffen(kopf.status);
  const wirdVerarbeitet = (teile ?? []).some((t) => teilauftragInVerarbeitung(t.status));
  const pausiert = kopf.istPausiert === true;

  return {
    zeigen,
    wirdVerarbeitet,
    pausieren: zeigen && !pausiert && !wirdVerarbeitet,
    fortsetzen: zeigen && pausiert,
    abbrechen: zeigen,
    loeschen: zeigen && !wirdVerarbeitet,
  };
}
